<script setup>
import {ElCol, ElRow, ElBadge, ElSelect, ElOption} from 'element-plus';
import 'element-plus/es/components/col/style/css';
import 'element-plus/es/components/row/style/css';
import 'element-plus/es/components/select/style/css';
import 'element-plus/es/components/option/style/css';
import 'element-plus/es/components/badge/style/css';
import DpIcon from '../DpIcon';
import {inject, ref, computed} from 'vue';
import { useI18n } from 'vue-i18n'
import {to} from 'await-to-js';

const spanSettings = {
  xl: {span: '6'},
  lg: {span: '6'},
  md: {span: '12'},
  sm: {span: '12'},
  xs: {span: '24'}
}
const spanSettingsWide = {
  xl: {span: '12'},
  lg: {span: '12'},
  md: {span: '24'},
  sm: {span: '24'},
  xs: {span: '24'}
}

const props = defineProps({
  companySelect: {type: Object},
  closeHandler: {type: Function},
  jurisdictionSelect: {type: Object},
  hasCompanySwitch: { type: Boolean, default: true }
})

const composables = inject('composables');
const {jurisdictionList, getJurisdictionByLocale, setJurisdiction} = composables.useJurisdictionSwitch();
const {locale, setLocaleMessage} = useI18n({ useScope: 'global' });
const { t } = useI18n()
const jurisdictionsAlias = t('ds.components.DpHeader.AvatarMenu.jurisdiction.selectValues');

async function handleJurisdictionSwitch(jurisdiction) {
  setJurisdiction(locale, jurisdiction)
  let translationValues, error;
  [error, translationValues] = await to(import('/src/locales/' + locale.value + '/translationValues.json'));
  if (error) {
    throw error;
  }
  let elementLocale;
  [error, elementLocale] = await to(import('@dutypay/components-library/src/locales/element-plus/' + locale.value + '.js'));
  if (error) {
    throw error;
  }
  setLocaleMessage(locale.value, Object.assign(elementLocale.default, translationValues.default));
}
const currentJurisdiction = ref(getJurisdictionByLocale(locale.value))

// Settings pages menu items
import {useEnvStore, useAuthStore, useCompanyStore} from "@dutypay/store-modules";
import {isEmpty, findIndex, get} from 'lodash'
const envStore = useEnvStore()
const authStore = useAuthStore()
const companyStore = useCompanyStore()
const router = inject('router')

const renderCompanySwitch = computed(() => {
  return companyStore.areCompaniesFetched
      && companyStore.companiesCountTotal > 1
      && !isEmpty(companyStore.selectedCompany);
});

const accountSettings = computed(() => [
  {
    label: t("accountSettings.accountInformation.linkLabel"),
    action: function() { router.push({ path: "/account-settings/account-information" }) }
  },
  {
    label: t("accountSettings.securitySettings.linkLabel"),
    action: function() { router.push({ path: "/account-settings/security-settings" }) }
  },
  {
    label: t('accountSettings.logout.linkLabel'),
    action: function() { window.location.href = envStore.ssoUrl + '/logout#i18n=' + locale.value }
  }
]);

const companySettings = computed(() => {
  let companySettings = [];
  if (envStore.debug
      || ( !isEmpty(authStore.user) && authStore.user.roles.indexOf('global:admin') > -1 )
      || ( ( !isEmpty(authStore.user) && authStore.user.roles.indexOf(`company:${companyStore.selectedCompany.id}:admin`) > -1) )) {
    companySettings.push({
      label: t("companySettings.integrations.linkLabel"),
      action: function() { router.push({ path: "/company-settings/integrations" }) }
    })
  }
  companySettings.push({
    label: t("companySettings.companyInformation.linkLabel"),
    action: function() { router.push({path: "/company-settings/company-information" }) }
  })
  return companySettings;
});

const applicationSettings = [];

const developerSettings = computed(() => {
  let developerSettings = [];
  if (!isEmpty(authStore.user)
    && findIndex(authStore.user.roles, (item) => {return item === 'global:admin' || item === 'global:developer'}) !== -1) {
    developerSettings.push({
      label: t("developerSettings.translationReport.linkLabel"),
      action: function() { router.push({ path: "/developer-settings/translation-report" }) }
    })
    developerSettings.push({
      label: t("developerSettings.translationEmailReportOverview.linkLabel"),
      action: function() { router.push({ path: "/developer-settings/translation-email-report-overview" }) }
    })
  }
  return developerSettings;
})

const adminSettings = computed(() => {
  let adminSettings = [];
  if (!isEmpty(authStore.user)
    && findIndex(authStore.user.roles, (item) => {return item === 'global:admin'}) !== -1) {
    adminSettings.push({
      label: t("adminSettings.companyAdministration.linkLabel"),
      action: function() { router.push({ path: "/admin-settings/companies" }) }
    });
    adminSettings.push({
      label: t("adminSettings.userAdministration.linkLabel"),
      action: function() { router.push({ path: "/admin-settings/users" }) }
    });
  }
  return adminSettings;
});

const legalLinks = computed(() => {
  let links = [];
  links.push({
    path: '/imprint',
    label: t('legalLinks.imprint.linkLabel')
  }, {
    path: '/privacy-policy',
    label: t('legalLinks.privacyPolicy.linkLabel')
  });
  if (envStore.debug) {
    links.push({
      path: '/terms-and-conditions',
      label: t('legalLinks.termsAndConditions.linkLabel')
    });
  }
  links.push({
    label: `${t('copyrightNotice.label', {currentDateDisplayOnlyYear: new Date().getFullYear()})}`
  });
  return links;
})

const notifications = computed(() => {
  return undefined;
})

function dismissAll() {
}
function openNotification() {
}
async function callAction(action) {

  await action();
}
</script>
<script>
export default {
  name: "AvatarMenu"
}
</script>

<template>
  <div ref="avatarMenu">
    <div class="avatar-menu__wrapper">

      <div class="avatar-menu__container" v-if="!isEmpty(notifications)">
        <el-row>
          <el-col :span="24">
            <el-row>
              <el-col :span="12">
                <span class="avatar-menu__category">{{t('notifications')}}</span>
              </el-col>
              <el-col :span="12">
                <dp-button style="float: right" size="small" type="text" @click="dismissAll()"
                           @keypress.enter="dismissAll()" tabindex="0">
                  <el-row type="flex">
                    <el-col>
                      <DpIcon icon="close"
                              color="#212121"
                              :size="12"
                              float="left"/>
                    </el-col>
                    <el-col>
                      {{t('dismissAll.label')}}
                    </el-col>
                  </el-row>
                </dp-button>
              </el-col>
            </el-row>

            <ul class="avatar-menu__items">
              <li v-for="(notification, index) in notifications"
                  :key="`notifications-${index}`"
                  style="display: block;"
                  @click="openNotification()"
                  @keypress.enter="openNotification()"
                  tabindex="0">
                <span>
                  {{notification.message}}
                </span>
              </li>
            </ul>
          </el-col>
        </el-row>
      </div>

      <div class="avatar-menu__container">
        <el-row type="flex" style="flex-direction: row-reverse; flex-wrap: wrap;">
          <el-col v-if="hasCompanySwitch"
                  :xs="spanSettingsWide.xs"
                  :sm="spanSettingsWide.sm"
                  :md="spanSettingsWide.md"
                  :lg="spanSettingsWide.lg"
                  :xl="spanSettingsWide.xl"
                  class="divider hidden-sm-and-up">
            <div class="avatar-menu__category">{{t('company.label')}}</div>
            <el-tag v-if="companyStore.companiesCountTotal === 0" type="danger" style="margin: .5em 0;">
              {{t('feedforward.noCompany.message')}}
            </el-tag>
            <DpSelectCompanySwitch v-if="renderCompanySwitch"
                                  v-on:switch="props.closeHandler()"
                                   style="width: 100%"
                                   popper-class="company-select__select__popper-dropdown"/>
            <ul v-if="!renderCompanySwitch && companyStore.hasSelectedCompany" class="avatar-menu__items">
              <li>{{companyStore.selectedCompany.name.legal}}</li>
            </ul>
          </el-col>

          <el-col :xs="spanSettings.xs"
                  :sm="spanSettings.sm"
                  :md="spanSettings.md"
                  :lg="spanSettings.lg"
                  :xl="spanSettings.xl"
                  class="divider"
                  v-if="!isEmpty(accountSettings)">
            <div class="avatar-menu__category">{{t('accountSettings.label')}}</div>

            <div v-if="!isEmpty(jurisdictionList)">
              <el-select v-model="currentJurisdiction"
                         value-key="i18n"
                         style="width: 100%"
                         v-on:change="handleJurisdictionSwitch($event)"
                         filterable
                         class="jurisdiction-select__select"
                         popper-class="jurisdiction-select__select__popper-dropdown">
                <template #prefix>
                  <span class="jurisdiction-select__prefix"><DpFlag :flagISOCode="getJurisdictionByLocale(locale).flagISOCode"/></span>
                </template>
                <el-option
                    v-for="(jurisdiction, index) in jurisdictionList"
                    :key="`select-jurisdiction-${index}`"
                    :label="t(`ds.components.DpHeader.AvatarMenu.jurisdiction.selectValues-${jurisdiction.translationKeyId}`)"
                    :value="jurisdiction">
                  <span class="jurisdiction-select__option__flag"><DpFlag :flagISOCode="jurisdiction.flagISOCode"/></span>
                  <span class="jurisdiction-select__option__translation">{{t(`ds.components.DpHeader.AvatarMenu.jurisdiction.selectValues-${jurisdiction.translationKeyId}`)}}</span>
                </el-option>
              </el-select>
            </div>

            <ul class="avatar-menu__items">
              <li v-for="(page, index) in accountSettings"
                  :key="`account-settings-${index}`"
                  :class="{'has-notification': page.notify}"
                  v-on:click="callAction(page.action)"
                  v-on:keypress.enter="callAction(page.action)"
                  tabindex="0">
                {{page.label}}&nbsp;<el-badge v-if="page.notify" is-dot class="mark"/>
              </li>
            </ul>
          </el-col>

          <el-col :xs="spanSettings.xs"
                  :sm="spanSettings.sm"
                  :md="spanSettings.md"
                  :lg="spanSettings.lg"
                  :xl="spanSettings.xl"
                  class="divider"
                  v-if="!isEmpty(applicationSettings)">
            <div class="avatar-menu__category">{{t('productSettings.label', { productName: envStore.applicationName })}}</div>
            <ul class="avatar-menu__items">
              <li v-for="(page, index) in applicationSettings"
                  :key="`product-settings-${index}`"
                  :class="{'has-notification': page.notify}"
                  v-on:click="callAction(page.action)"
                  v-on:keypress.enter="callAction(page.action)"
                  tabindex="0">
                {{page.label}}&nbsp;<el-badge v-if="page.notify" is-dot class="mark"/>
              </li>
            </ul>
          </el-col>

          <el-col :xs="spanSettings.xs"
                  :sm="spanSettings.sm"
                  :md="spanSettings.md"
                  :lg="spanSettings.lg"
                  :xl="spanSettings.xl"
                  class="divider"
                  v-if="!isEmpty(companySettings)">
            <div class="avatar-menu__category">{{t('companySettings.label')}}</div>
            <ul class="avatar-menu__items">
              <li v-for="(page, index) in companySettings"
                  :key="`company-settings-${index}`"
                  :class="{'has-notification': page.notify}"
                  v-on:click="callAction(page.action)"
                  v-on:keypress.enter="callAction(page.action)"
                  tabindex="0">
                {{page.label}}&nbsp;<el-badge v-if="page.notify" is-dot class="mark"/>
              </li>
            </ul>
          </el-col>

          <el-col :xs="spanSettings.xs"
                  :sm="spanSettings.sm"
                  :md="spanSettings.md"
                  :lg="spanSettings.lg"
                  :xl="spanSettings.xl"
                  class="divider"
                  v-if="!isEmpty(developerSettings)">
            <div class="avatar-menu__category">{{t('developerSettings.label')}}</div>
            <ul class="avatar-menu__items">
              <li v-for="(page, index) in developerSettings"
                  :key="`developer-settings-${index}`"
                  :class="{'has-notification': page.notify}"
                  v-on:click="callAction(page.action)"
                  v-on:keypress.enter="callAction(page.action)"
                  tabindex="0">
                {{page.label}}&nbsp;<el-badge v-if="page.notify" is-dot class="mark"/>
              </li>
            </ul>
          </el-col>

          <el-col :xs="spanSettings.xs"
                  :sm="spanSettings.sm"
                  :md="spanSettings.md"
                  :lg="spanSettings.lg"
                  :xl="spanSettings.xl"
                  class="divider"
                  v-if="!isEmpty(adminSettings)">
            <div class="avatar-menu__category">{{t('adminSettings.label')}}</div>
            <ul class="avatar-menu__items">
              <li v-for="(page, index) in adminSettings"
                  :key="`admin-settings-${index}`"
                  :class="{'has-notification': page.notify}"
                  v-on:click="callAction(page.action)"
                  v-on:keypress.enter="callAction(page.action)"
                  tabindex="0">
                {{page.label}}&nbsp;<el-badge v-if="page.notify" is-dot class="mark"/>
              </li>
            </ul>
          </el-col>
        </el-row>

        <div class="avatar-menu__close"
             @click="closeHandler()"
             @keypress="closeHandler()"
             tabindex="0"
             :style="{'cursor': 'pointer'}">
          <DpIcon icon="close" color="#212121" :size="24" :margin="false">
          </DpIcon>
        </div>
      </div>

      <div class="avatar-menu__legal">
        <ul v-if="!isEmpty(legalLinks)">
          <li v-for="(link, index) in legalLinks" :key="index" tabindex="0">
            <router-link v-if="!isEmpty(link.path)" @click.native="closeHandler" :to="{path: link.path}">{{link.label}}</router-link>
            <span v-else>{{link.label}}</span>
          </li>
        </ul>
      </div>

    </div>
  </div>
</template>

<style lang="scss">
.avatar-menu__container {
  .dp-input, .el-input__inner,
  .el-select .el-input__inner,
  .el-textarea__inner {
    border-color: #DCDFE6;
    color: #606266;
  }

  .jurisdiction-select__select .el-input .el-input__prefix {
    inset-inline-start: .8em;
  }
}

.company-select__select__popper-dropdown,
.jurisdiction-select__select__popper-dropdown {
  z-index: 3006 !important;
}
</style>

<style scoped lang="scss">
.jurisdiction-select__prefix {
  line-height: 2.6rem;
}

.avatar-menu__wrapper {
  padding-bottom: 1rem;
  padding-top: 1rem;

  .avatar-menu__legal {
    color: #f7f7f7;
    font-size: 0.714rem;

    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: inline-block;

      li {
        display: inline-block;
        word-break: keep-all;
        padding: 0.33em;

        &:not(:last-child) {
          margin-inline-end: 1.5em;
        }

        a,
        a:hover,
        a:active,
        a:visited {
          color: #f7f7f7;
          border-bottom: 0;
        }
      }
    }
  }

  .avatar-menu__container {
    position: relative;
    padding-top: .9rem;
    padding-bottom: .4rem;
    padding-inline-start: 1.4rem;
    padding-inline-end: .9rem;
    border-top: 2px solid #ffffff;
    border-bottom: 2px solid #cdcdcd;
    -webkit-border-radius: 10px;
    -moz-border-radius: 10px;
    border-radius: 10px;
    box-shadow: 0 0 24px rgba(0, 0, 0, .18);
    background-color: rgba(255, 255, 255, 0.95);

    .avatar-menu__close {
      position: absolute;
      inset-inline-end: 0.9rem;
      inset-block-start: 0.4rem;
    }

    &:not(:last-child) {
      margin-bottom: 1rem;
    }

    .el-row .el-col.divider {
      padding-top: 0;
      padding-bottom: 1rem;
      padding-inline-end: 1rem;
      padding-inline-start: 0;
    }

    .avatar-menu__category {
      font-size: 0.857rem;
      color: #616161;
      padding: .8em;
    }

    ul.avatar-menu__items {
      padding: 0;
      margin: 0;

      li {
        font-weight: 600;
        padding: .33em;
        margin: .25em;
        display: block;
        cursor: pointer;
        -webkit-border-radius: 6px;
        -moz-border-radius: 6px;
        border-radius: 6px;

        &.selected,
        &:hover,
        &:focus {
          background-color: #ffffff;
        }

        &.has-notification {
          font-weight: 700;
        }
      }
    }
  }
}
</style>

<style scoped lang="scss">

@media only screen and (max-width: 767px) {
  .avatar-menu__wrapper {

    .avatar-menu__legal {
      font-size: 12px;

      ul {
        list-style: none;
        margin: 2rem 0 0 0;
        padding: 0;
        display: block;

        li {
          display: block;
          word-break: keep-all;
          margin: 1rem .7rem;

          a,
          a:hover,
          a:active,
          a:visited {
            border-bottom: 0;
          }
        }
      }
    }

    .avatar-menu__container {
      padding-top: .3rem;
      padding-bottom: .3rem;
      padding-inline-end: .6em;
      padding-inline-start: .7em;

      .avatar-menu__category {
        font-size: 1.2rem;
      }

      ul.avatar-menu__items {
        margin: 0;
        margin-top: 1rem;
        padding: 0;

        li {
          padding: .75rem .7rem;
          margin: 0 0 1em 0;
        }
      }
    }
  }
}
</style>

<i18n>
{
  "ar-AE": {
    "company": {
      "label": "الشركات"
    },
    "accountSettings": {
      "label": "إعدادات حساب DutyPay",
      "accountInformation": {
        "linkLabel": "حساب DutyPay"
      },
      "securitySettings": {
        "linkLabel": "إعدادات الأمان"
      },
      "logout": {
        "linkLabel": "تسجيل الخروج"
      }
    },
    "productSettings": {
      "label": "{productName} الإعدادات"
    },
    "feedforward": {
      "noCompany": {
        "message": "لا توجد شركة معينة. الرجاء إضافة شركة."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "إعدادات الشركة",
      "integrations": {
        "linkLabel": "إعدادات التكامل"
      },
      "companyInformation": {
        "linkLabel": "حساب الشركات"
      }
    },
    "developerSettings": {
      "label": "إعدادات المطور",
      "translationReport": {
        "linkLabel": "تقرير الترجمة"
      },
      "translationEmailReportOverview": {
        "linkLabel": "الرسائل الصادرة"
      }
    },
    "adminSettings": {
      "label": "إعدادات المسؤول",
      "companyAdministration": {
        "linkLabel": "إدارة حساب الشركة"
      },
      "userAdministration": {
        "linkLabel": "إدارة المستخدم"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "حماية البيانات"
      },
      "imprint": {
        "linkLabel": "معلومات الشركة"
      },
      "termsAndConditions": {
        "linkLabel": "تعليمات الاستخدام"
      }
    },
    "copyrightNotice": {
      "label": "DutyPay GmbH - {currentDateDisplayOnlyYear} - 2013 ©"
    }
  },
  "ar-EG": {
    "company": {
      "label": "الشركات"
    },
    "accountSettings": {
      "label": "إعدادات حساب DutyPay",
      "accountInformation": {
        "linkLabel": "حساب DutyPay"
      },
      "securitySettings": {
        "linkLabel": "إعدادات الأمان"
      },
      "logout": {}
    },
    "productSettings": {},
    "feedforward": {
      "noCompany": {}
    },
    "companySettings": {
      "label": "إعدادات الشركة",
      "integrations": {
        "linkLabel": "إعدادات التكامل"
      },
      "companyInformation": {
        "linkLabel": "حساب الشركات"
      }
    },
    "developerSettings": {
      "label": "إعدادات المطور",
      "translationReport": {},
      "translationEmailReportOverview": {}
    },
    "adminSettings": {
      "label": "إعدادات المسؤول",
      "companyAdministration": {},
      "userAdministration": {}
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "حماية البيانات"
      },
      "imprint": {},
      "termsAndConditions": {}
    },
    "copyrightNotice": {}
  },
  "ar-SA": {
    "company": {
      "label": "الشركات"
    },
    "accountSettings": {
      "label": "إعدادات حساب DutyPay",
      "accountInformation": {
        "linkLabel": "حساب DutyPay"
      },
      "securitySettings": {
        "linkLabel": "إعدادات الأمان"
      },
      "logout": {}
    },
    "productSettings": {},
    "feedforward": {
      "noCompany": {}
    },
    "companySettings": {
      "label": "إعدادات الشركة",
      "integrations": {
        "linkLabel": "إعدادات التكامل"
      },
      "companyInformation": {
        "linkLabel": "حساب الشركات"
      }
    },
    "developerSettings": {
      "label": "إعدادات المطور",
      "translationReport": {},
      "translationEmailReportOverview": {}
    },
    "adminSettings": {
      "label": "إعدادات المسؤول",
      "companyAdministration": {},
      "userAdministration": {}
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "حماية البيانات"
      },
      "imprint": {},
      "termsAndConditions": {}
    },
    "copyrightNotice": {}
  },
  "de-DE": {
    "company": {
      "label": "Unternehmen"
    },
    "accountSettings": {
      "label": "DutyPay-Kontoeinstellungen",
      "accountInformation": {
        "linkLabel": "DutyPay-Konto"
      },
      "securitySettings": {
        "linkLabel": "Sicherheitseinstellungen"
      },
      "logout": {
        "linkLabel": "Abmelden"
      }
    },
    "productSettings": {
      "label": "{productName} Einstellungen"
    },
    "feedforward": {
      "noCompany": {
        "message": "Kein zugewiesenes Unternehmen. Bitte fügen Sie ein Unternehmen hinzu."
      }
    },
    "notifications": "ds.components.DpHeader.AvatarMenu.notifications",
    "companySettings": {
      "label": "Unternehmenseinstellungen",
      "integrations": {
        "linkLabel": "Integrationseinstellungen"
      },
      "companyInformation": {
        "linkLabel": "Unternehmenskonto"
      }
    },
    "developerSettings": {
      "label": "Entwicklereinstellungen",
      "translationReport": {
        "linkLabel": "Übersetzungsbericht"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Ausgehende Nachricht"
      }
    },
    "adminSettings": {
      "label": "Administratoreinstellungen",
      "companyAdministration": {
        "linkLabel": "Unternehmenskontenverwaltung"
      },
      "userAdministration": {
        "linkLabel": "Benutzerverwaltung"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Datenschutz"
      },
      "imprint": {
        "linkLabel": "Impressum"
      },
      "termsAndConditions": {
        "linkLabel": "Nutzungsbedingungen"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "de-AT": {
    "company": {
      "label": "Unternehmen"
    },
    "accountSettings": {
      "label": "DutyPay-Kontoeinstellungen",
      "accountInformation": {
        "linkLabel": "DutyPay-Konto"
      },
      "securitySettings": {
        "linkLabel": "Sicherheitseinstellungen"
      },
      "logout": {
        "linkLabel": "Abmelden"
      }
    },
    "productSettings": {
      "label": "{productName} Einstellungen"
    },
    "feedforward": {
      "noCompany": {
        "message": "Kein zugewiesenes Unternehmen. Bitte fügen Sie ein Unternehmen hinzu."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Unternehmenseinstellungen",
      "integrations": {
        "linkLabel": "Integrationseinstellungen"
      },
      "companyInformation": {
        "linkLabel": "Unternehmenskonto"
      }
    },
    "developerSettings": {
      "label": "Entwicklereinstellungen",
      "translationReport": {
        "linkLabel": "Übersetzungsbericht"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Ausgehende Nachricht"
      }
    },
    "adminSettings": {
      "label": "Administratoreinstellungen",
      "companyAdministration": {
        "linkLabel": "Unternehmenskontenverwaltung"
      },
      "userAdministration": {
        "linkLabel": "Benutzerverwaltung"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Datenschutz"
      },
      "imprint": {
        "linkLabel": "Impressum"
      },
      "termsAndConditions": {
        "linkLabel": "Nutzungsbedingungen"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "de-CH": {
    "company": {
      "label": "Unternehmen"
    },
    "accountSettings": {
      "label": "DutyPay-Kontoeinstellungen",
      "accountInformation": {
        "linkLabel": "DutyPay-Konto"
      },
      "securitySettings": {
        "linkLabel": "Sicherheitseinstellungen"
      },
      "logout": {
        "linkLabel": "Abmelden"
      }
    },
    "productSettings": {
      "label": "{productName} Einstellungen"
    },
    "feedforward": {
      "noCompany": {
        "message": "Kein zugewiesenes Unternehmen. Bitte fügen Sie ein Unternehmen hinzu."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Unternehmenseinstellungen",
      "integrations": {
        "linkLabel": "Integrationseinstellungen"
      },
      "companyInformation": {
        "linkLabel": "Unternehmenskonto"
      }
    },
    "developerSettings": {
      "label": "Entwicklereinstellungen",
      "translationReport": {
        "linkLabel": "Übersetzungsbericht"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Ausgehende Nachricht"
      }
    },
    "adminSettings": {
      "label": "Administratoreinstellungen",
      "companyAdministration": {
        "linkLabel": "Unternehmenskontenverwaltung"
      },
      "userAdministration": {
        "linkLabel": "Benutzerverwaltung"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Datenschutz"
      },
      "imprint": {
        "linkLabel": "Impressum"
      },
      "termsAndConditions": {
        "linkLabel": "Nutzungsbedingungen"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "en-US": {
    "company": {
      "label": "Company"
    },
    "accountSettings": {
      "label": "DutyPay Account Settings",
      "accountInformation": {
        "linkLabel": "DutyPay Account"
      },
      "securitySettings": {
        "linkLabel": "Security Settings"
      },
      "logout": {
        "linkLabel": "Logout"
      }
    },
    "productSettings": {
      "label": "{productName} Settings"
    },
    "feedforward": {
      "noCompany": {
        "message": "No assigned company. Please add a company"
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Company Settings",
      "integrations": {
        "linkLabel": "Integration Settings"
      },
      "companyInformation": {
        "linkLabel": "Company Account"
      }
    },
    "developerSettings": {
      "label": "Developer Settings",
      "translationReport": {
        "linkLabel": "Translation report"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Outbound message"
      }
    },
    "adminSettings": {
      "label": "Administrator Settings",
      "companyAdministration": {
        "linkLabel": "Company account administration"
      },
      "userAdministration": {
        "linkLabel": "User administration"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Data Protection"
      },
      "imprint": {
        "linkLabel": "Company Information"
      },
      "termsAndConditions": {
        "linkLabel": "Terms of Use"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "en-GB": {
    "company": {
      "label": "Company"
    },
    "accountSettings": {
      "label": "DutyPay Account Settings",
      "accountInformation": {
        "linkLabel": "DutyPay Account"
      },
      "securitySettings": {
        "linkLabel": "Security Settings"
      },
      "logout": {
        "linkLabel": "Logout"
      }
    },
    "productSettings": {
      "label": "{productName} Settings"
    },
    "feedforward": {
      "noCompany": {
        "message": "No assigned company. Please add a company"
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Company Settings",
      "integrations": {
        "linkLabel": "Integration Settings"
      },
      "companyInformation": {
        "linkLabel": "Company Account"
      }
    },
    "developerSettings": {
      "label": "Developer Settings",
      "translationReport": {
        "linkLabel": "Translation report"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Outbound Message"
      }
    },
    "adminSettings": {
      "label": "Administrator Settings",
      "companyAdministration": {
        "linkLabel": "Company account administration"
      },
      "userAdministration": {
        "linkLabel": "User administration"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Data Protection"
      },
      "imprint": {
        "linkLabel": "Company Information"
      },
      "termsAndConditions": {
        "linkLabel": "Terms of Use"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "it-IT": {
    "company": {
      "label": "Anagrafica Azienda"
    },
    "accountSettings": {
      "label": "Impostazioni dell'Account DutyPay",
      "accountInformation": {
        "linkLabel": "Conto DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Impostazioni di Sicurezza"
      },
      "logout": {
        "linkLabel": "Logout"
      }
    },
    "productSettings": {
      "label": "{productName} Impostazioni"
    },
    "feedforward": {
      "noCompany": {
        "message": "Nessuna azienda assegnata. Aggiungi un'azienda."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Impostazioni Azienda",
      "integrations": {
        "linkLabel": "Impostazioni di integrazione"
      },
      "companyInformation": {
        "linkLabel": "Account Aziendale"
      }
    },
    "developerSettings": {
      "label": "Impostazioni sviluppatore",
      "translationReport": {
        "linkLabel": "Rapporto di traduzione"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Messaggi in uscita"
      }
    },
    "adminSettings": {
      "label": "Impostazioni amministratore",
      "companyAdministration": {
        "linkLabel": "Amministrazione dell'account aziendale"
      },
      "userAdministration": {
        "linkLabel": "Amministrazione utenti"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Protezione Dati"
      },
      "imprint": {
        "linkLabel": "Chi siamo"
      },
      "termsAndConditions": {
        "linkLabel": "Condizioni d'uso"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "fr-FR": {
    "company": {
      "label": "Entreprises"
    },
    "accountSettings": {
      "label": "Paramètres du Compte DutyPay",
      "accountInformation": {
        "linkLabel": "Compte DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Les Paramètres de Sécurité"
      },
      "logout": {
        "linkLabel": "Se déconnecter"
      }
    },
    "productSettings": {
      "label": "{productName} Paramètres"
    },
    "feedforward": {
      "noCompany": {
        "message": "Aucune entreprise assignée. Veuillez ajouter une entreprise."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Paramètres de l'Entreprise",
      "integrations": {
        "linkLabel": "Paramètres d'intégration"
      },
      "companyInformation": {
        "linkLabel": "Compte d'Entreprise"
      }
    },
    "developerSettings": {
      "label": "Paramètres du développeur",
      "translationReport": {
        "linkLabel": "Rapport de traduction"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Messages sortants"
      }
    },
    "adminSettings": {
      "label": "Paramètres administrateur",
      "companyAdministration": {
        "linkLabel": "Gestion des comptes d'entreprise"
      },
      "userAdministration": {
        "linkLabel": "Gestion des utilisateurs"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Protection des Données"
      },
      "imprint": {
        "linkLabel": "Mentions Légales"
      },
      "termsAndConditions": {
        "linkLabel": "Conditions d'utilisation"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "es-ES": {
    "company": {
      "label": "Compañías"
    },
    "accountSettings": {
      "label": "Configuración de la Cuenta DutyPay",
      "accountInformation": {
        "linkLabel": "Cuenta DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Configuraciones de Seguridad"
      },
      "logout": {
        "linkLabel": "Cerrar sesión"
      }
    },
    "productSettings": {
      "label": "{productName} Configuraciones"
    },
    "feedforward": {
      "noCompany": {
        "message": "Sin empresa asignada. Añade una empresa."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Configuración de la Empresa",
      "integrations": {
        "linkLabel": "Configuración de integración"
      },
      "companyInformation": {
        "linkLabel": "Cuenta Corporativa"
      }
    },
    "developerSettings": {
      "label": "Configuración de desarrollador",
      "translationReport": {
        "linkLabel": "Informe de traducción"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Mensajes salientes"
      }
    },
    "adminSettings": {
      "label": "Configuraciones de administrador",
      "companyAdministration": {
        "linkLabel": "Administración de cuentas de empresa"
      },
      "userAdministration": {
        "linkLabel": "Administración de Usuario"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Protección de Datos"
      },
      "imprint": {
        "linkLabel": "Información sobre Empresa"
      },
      "termsAndConditions": {
        "linkLabel": "Términos de Uso"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "pl-PL": {
    "company": {
      "label": "Firmy"
    },
    "accountSettings": {
      "label": "Ustawienia Konta DutyPay",
      "accountInformation": {
        "linkLabel": "Konto DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Ustawienia Bezpieczeństwa"
      },
      "logout": {
        "linkLabel": "Wyloguj"
      }
    },
    "productSettings": {
      "label": "{productName} Ustawienia"
    },
    "feedforward": {
      "noCompany": {
        "message": "Brak przypisanej firmy. Dodaj firmę."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Ustawienia Firmy",
      "integrations": {
        "linkLabel": "Ustawienia integracji"
      },
      "companyInformation": {
        "linkLabel": "Konto Firmowe"
      }
    },
    "developerSettings": {
      "label": "Ustawienia programisty",
      "translationReport": {
        "linkLabel": "Raport z tłumaczenia"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Wiadomości wychodzące"
      }
    },
    "adminSettings": {
      "label": "Ustawienia administratora",
      "companyAdministration": {
        "linkLabel": "Zarządzanie kontem firmowym"
      },
      "userAdministration": {
        "linkLabel": "Administracja użytkownika"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Ochrona Danych"
      },
      "imprint": {
        "linkLabel": "Informacja o firmie"
      },
      "termsAndConditions": {
        "linkLabel": "Warunki korzystania"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "nl-NL": {
    "company": {
      "label": "Bedrijven"
    },
    "accountSettings": {
      "label": "DutyPay-Accountinstellingen",
      "accountInformation": {
        "linkLabel": "DutyPay-Account"
      },
      "securitySettings": {
        "linkLabel": "Beveiligingsinstellingen"
      },
      "logout": {
        "linkLabel": "Afmelden"
      }
    },
    "productSettings": {
      "label": "{productName} Instellingen"
    },
    "feedforward": {
      "noCompany": {
        "message": "Geen toegewezen bedrijf. Voeg een bedrijf toe."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Bedrijfsinstellingen",
      "integrations": {
        "linkLabel": "Integratie-instellingen"
      },
      "companyInformation": {
        "linkLabel": "Zakelijke Rekening"
      }
    },
    "developerSettings": {
      "label": "Ontwikkelaarsinstellingen",
      "translationReport": {
        "linkLabel": "Vertaalrapport"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Uitgaande berichten"
      }
    },
    "adminSettings": {
      "label": "Beheerdersinstellingen",
      "companyAdministration": {
        "linkLabel": "Bedrijfsaccount administratie"
      },
      "userAdministration": {
        "linkLabel": "Gebruikersadministratie"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Gegevensbescherming"
      },
      "imprint": {
        "linkLabel": "Bedrijfsinformatie"
      },
      "termsAndConditions": {
        "linkLabel": "Gebruiksvoorwaarden"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "bg-BG": {
    "company": {
      "label": "Фирми"
    },
    "accountSettings": {
      "label": "Настройки на DutyPay Aкаунт",
      "accountInformation": {
        "linkLabel": "DutyPay Сметка"
      },
      "securitySettings": {
        "linkLabel": "Настройки на Cигурността"
      },
      "logout": {
        "linkLabel": "Излизане от профила"
      }
    },
    "productSettings": {
      "label": "{productName} Настройки"
    },
    "feedforward": {
      "noCompany": {
        "message": "Няма определена компания. Моля, добавете фирма."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Настройки на Kомпанията",
      "integrations": {
        "linkLabel": "Настройки за интеграция"
      },
      "companyInformation": {
        "linkLabel": "Корпоративна Cметка"
      }
    },
    "developerSettings": {
      "label": "Настройки за програмисти",
      "translationReport": {
        "linkLabel": "Доклад за превода"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Изходящи съобщения"
      }
    },
    "adminSettings": {
      "label": "Настройки на администратора",
      "companyAdministration": {
        "linkLabel": "Администрация на фирмена сметка"
      },
      "userAdministration": {
        "linkLabel": "Администрация на потребителя"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Защита на Данни"
      },
      "imprint": {
        "linkLabel": "Автор"
      },
      "termsAndConditions": {
        "linkLabel": "Условия за ползване"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "bs-BA": {
    "company": {
      "label": "Kompanije"
    },
    "accountSettings": {
      "label": "Postavke DutyPay Računa",
      "accountInformation": {
        "linkLabel": "DutyPay Račun"
      },
      "securitySettings": {
        "linkLabel": "Sigurnosne Postavke"
      },
      "logout": {
        "linkLabel": "Odjavite se"
      }
    },
    "productSettings": {
      "label": "{productName} Podešavanja"
    },
    "feedforward": {
      "noCompany": {
        "message": "Nema dodijeljene kompanije. Molimo dodajte kompaniju."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Postavke Kompanije",
      "integrations": {
        "linkLabel": "Postavke integracije"
      },
      "companyInformation": {
        "linkLabel": "Korporativni Račun"
      }
    },
    "developerSettings": {
      "label": "Postavke programera",
      "translationReport": {
        "linkLabel": "Izvještaj o prevođenju"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Odlazne poruke"
      }
    },
    "adminSettings": {
      "label": "Postavke administratora",
      "companyAdministration": {
        "linkLabel": "Administracija računa kompanije"
      },
      "userAdministration": {
        "linkLabel": "Administracija korisnika"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Zaštita Podataka"
      },
      "imprint": {
        "linkLabel": "Impressum"
      },
      "termsAndConditions": {
        "linkLabel": "Pravila korištenja"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "da-DK": {
    "company": {
      "label": "Virksomheder"
    },
    "accountSettings": {
      "label": "DutyPay-Kontoindstillinger",
      "accountInformation": {
        "linkLabel": "DutyPay Konto"
      },
      "securitySettings": {
        "linkLabel": "Sikkerhedsindstillinger"
      },
      "logout": {
        "linkLabel": "Log ud"
      }
    },
    "productSettings": {
      "label": "{productName} Indstillinger"
    },
    "feedforward": {
      "noCompany": {
        "message": "Intet tildelt firma. Tilføj venligst et firma."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Virksomhedsindstillinger",
      "integrations": {
        "linkLabel": "Integrationsindstillinger"
      },
      "companyInformation": {
        "linkLabel": "Virksomhedskonto"
      }
    },
    "developerSettings": {
      "label": "Udviklerindstillinger",
      "translationReport": {
        "linkLabel": "Oversættelsesrapport"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Udgående beskeder"
      }
    },
    "adminSettings": {
      "label": "Administratorindstillinger",
      "companyAdministration": {
        "linkLabel": "Administration af virksomhedskonto"
      },
      "userAdministration": {
        "linkLabel": "Brugeradministration"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Data Beskyttelse"
      },
      "imprint": {
        "linkLabel": "Virksomhedsoplysninger"
      },
      "termsAndConditions": {
        "linkLabel": "Betingelser for brug"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "et-EE": {
    "company": {
      "label": "Ettevõtted"
    },
    "accountSettings": {
      "label": "DutyPay Konto Seaded",
      "accountInformation": {
        "linkLabel": "DutyPay Konto"
      },
      "securitySettings": {
        "linkLabel": "Turvaseaded"
      },
      "logout": {
        "linkLabel": "Logi välja"
      }
    },
    "productSettings": {
      "label": "{productName} Seadistused"
    },
    "feedforward": {
      "noCompany": {
        "message": "Määratud ettevõtet pole. Palun lisage ettevõte."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Ettevõtte Seaded",
      "integrations": {
        "linkLabel": "Integreerimise seaded"
      },
      "companyInformation": {
        "linkLabel": "Ettevõtte Konto"
      }
    },
    "developerSettings": {
      "label": "Arendaja seaded",
      "translationReport": {
        "linkLabel": "Tõlkearuanne"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Väljaminevad sõnumid"
      }
    },
    "adminSettings": {
      "label": "Administraatori seaded",
      "companyAdministration": {
        "linkLabel": "Ettevõtte konto haldamine"
      },
      "userAdministration": {
        "linkLabel": "Kasutaja haldus"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Andmekaitse"
      },
      "imprint": {
        "linkLabel": "Ettevõtte info"
      },
      "termsAndConditions": {
        "linkLabel": "Kasutustingimused"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "fi-FI": {
    "company": {
      "label": "Yritys"
    },
    "accountSettings": {
      "label": "DutyPay-Tilin Asetukset",
      "accountInformation": {
        "linkLabel": "DutyPay-Tili"
      },
      "securitySettings": {
        "linkLabel": "Turvallisuusasetukset"
      },
      "logout": {
        "linkLabel": "Kirjaudu ulos"
      }
    },
    "productSettings": {
      "label": "{productName} Asetukset"
    },
    "feedforward": {
      "noCompany": {
        "message": "Ei nimettyä yritystä. Lisää yritys."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Yrityksen Asetukset",
      "integrations": {
        "linkLabel": "Integrointiasetukset"
      },
      "companyInformation": {
        "linkLabel": "Yritystili"
      }
    },
    "developerSettings": {
      "label": "Kehittäjän asetukset",
      "translationReport": {
        "linkLabel": "Käännösraportti"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Lähtevät viestit"
      }
    },
    "adminSettings": {
      "label": "Järjestelmänvalvojan asetukset",
      "companyAdministration": {
        "linkLabel": "Yritystilin hallinta"
      },
      "userAdministration": {
        "linkLabel": "Käyttäjien hallinta"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Datan Suojelu"
      },
      "imprint": {
        "linkLabel": "Yrityksen tiedot"
      },
      "termsAndConditions": {
        "linkLabel": "Käyttöehdot"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "el-GR": {
    "company": {
      "label": "Εταιρείες"
    },
    "accountSettings": {
      "label": "Ρυθμίσεις Λογαριασμού DutyPay",
      "accountInformation": {
        "linkLabel": "Λογαριασμός DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Ρυθμίσεις Ασφαλείας"
      },
      "logout": {
        "linkLabel": "Αποσύνδεση"
      }
    },
    "productSettings": {
      "label": "{productName} Ρυθμίσεις"
    },
    "feedforward": {
      "noCompany": {
        "message": "Δεν έχει ανατεθεί εταιρεία. Προσθέστε μια εταιρεία."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Ρυθμίσεις Εταιρείας",
      "integrations": {
        "linkLabel": "Ρυθμίσεις ενοποίησης"
      },
      "companyInformation": {
        "linkLabel": "Εταιρικός Λογαριασμός"
      }
    },
    "developerSettings": {
      "label": "Ρυθμίσεις προγραμματιστή",
      "translationReport": {
        "linkLabel": "Έκθεση μετάφρασης"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Εξερχόμενα μηνύματα"
      }
    },
    "adminSettings": {
      "label": "Ρυθμίσεις διαχειριστή",
      "companyAdministration": {
        "linkLabel": "Διαχείριση εταιρικού λογαριασμού"
      },
      "userAdministration": {
        "linkLabel": "Διαχείριση χρηστών"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Προστασία Δεδομένων"
      },
      "imprint": {
        "linkLabel": "Στοιχεία της εταιρείας"
      },
      "termsAndConditions": {
        "linkLabel": "Οροι χρήσης"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "he-IL": {
    "company": {
      "label": "חברות"
    },
    "accountSettings": {
      "label": "הגדרות חשבון DutyPay",
      "accountInformation": {
        "linkLabel": "חשבון DutyPay"
      },
      "securitySettings": {
        "linkLabel": "הגדרות אבטחה"
      },
      "logout": {
        "linkLabel": "להתנתק"
      }
    },
    "productSettings": {
      "label": "{productName} הגדרות"
    },
    "feedforward": {
      "noCompany": {
        "message": "אין חברה שהוקצתה. אנא הוסף חברה."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "הגדרות חברה",
      "integrations": {
        "linkLabel": "הגדרות שילוב"
      },
      "companyInformation": {
        "linkLabel": "חשבון תאגידי"
      }
    },
    "developerSettings": {
      "label": "הגדרות מפתח",
      "translationReport": {
        "linkLabel": "דוח תרגום"
      },
      "translationEmailReportOverview": {
        "linkLabel": "הודעות יוצאות"
      }
    },
    "adminSettings": {
      "label": "הגדרות מנהל",
      "companyAdministration": {
        "linkLabel": "ניהול חשבון החברה"
      },
      "userAdministration": {
        "linkLabel": "ניהול משתמשים"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "הגנת מידע"
      },
      "imprint": {
        "linkLabel": "מידע על החברה"
      },
      "termsAndConditions": {
        "linkLabel": "תנאי שימוש"
      }
    },
    "copyrightNotice": {
      "label": "DutyPay GmbH - {currentDateDisplayOnlyYear} - 2013 ©"
    }
  },
  "ga-IE": {
    "company": {
      "label": "Cuideachtaí"
    },
    "accountSettings": {
      "label": "Socruithe Cuntais DutyPay",
      "accountInformation": {
        "linkLabel": "Cuntas DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Socruithe Slándála"
      },
      "logout": {
        "linkLabel": "Logáil amach"
      }
    },
    "productSettings": {
      "label": "{productName} Socruithe"
    },
    "feedforward": {
      "noCompany": {
        "message": "Gan aon chuideachta sannta. Cuir cuideachta le do thoil."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Socruithe Cuideachta",
      "integrations": {
        "linkLabel": "Socruithe comhtháthaithe"
      },
      "companyInformation": {
        "linkLabel": "Cuntas Corparáideach"
      }
    },
    "developerSettings": {
      "label": "Socruithe Forbróra",
      "translationReport": {
        "linkLabel": "Tuarascáil aistriúcháin"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Teachtaireachtaí Amach"
      }
    },
    "adminSettings": {
      "label": "Socruithe riarthóra",
      "companyAdministration": {
        "linkLabel": "Riarachán cuntas cuideachta"
      },
      "userAdministration": {
        "linkLabel": "Riarachán úsáideora"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Cosaint Sonraí"
      },
      "imprint": {
        "linkLabel": "Fógra Dlí"
      },
      "termsAndConditions": {
        "linkLabel": "Téarmaí Úsáide"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "ja-JP": {
    "company": {
      "label": "企業"
    },
    "accountSettings": {
      "label": "DutyPayアカウント設定",
      "accountInformation": {
        "linkLabel": "DutyPay 口座"
      },
      "securitySettings": {
        "linkLabel": "セキュリティ設定"
      },
      "logout": {
        "linkLabel": "ログアウト"
      }
    },
    "productSettings": {
      "label": "{productName} 設定"
    },
    "feedforward": {
      "noCompany": {
        "message": "割り当てられた会社はありません。 会社を追加してください。"
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "会社の設定",
      "integrations": {
        "linkLabel": "統合設定"
      },
      "companyInformation": {
        "linkLabel": "企業アカウント"
      }
    },
    "developerSettings": {
      "label": "開発者設定",
      "translationReport": {
        "linkLabel": "翻訳レポート"
      },
      "translationEmailReportOverview": {
        "linkLabel": "送信メッセージ"
      }
    },
    "adminSettings": {
      "label": "管理者設定",
      "companyAdministration": {
        "linkLabel": "企業アカウント管理"
      },
      "userAdministration": {
        "linkLabel": "ユーザー管理"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "データ保護"
      },
      "imprint": {
        "linkLabel": "企業情報"
      },
      "termsAndConditions": {
        "linkLabel": "利用規約"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "hr-HR": {
    "company": {
      "label": "Tvrtka"
    },
    "accountSettings": {
      "label": "Postavke DutyPay Računa",
      "accountInformation": {
        "linkLabel": "DutyPay Račun"
      },
      "securitySettings": {
        "linkLabel": "Sigurnosne Postavke"
      },
      "logout": {
        "linkLabel": "Odjavite se"
      }
    },
    "productSettings": {
      "label": "{productName} Postavke"
    },
    "feedforward": {
      "noCompany": {
        "message": "Nema dodijeljene tvrtke. Molimo dodajte tvrtku."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Postavke Tvrtke",
      "integrations": {
        "linkLabel": "Postavke integracije"
      },
      "companyInformation": {
        "linkLabel": "Korporativni Račun"
      }
    },
    "developerSettings": {
      "label": "Postavke programera",
      "translationReport": {
        "linkLabel": "Izvještaj o prijevodu"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Odlazne poruke"
      }
    },
    "adminSettings": {
      "label": "Postavke administratora",
      "companyAdministration": {
        "linkLabel": "Administracija računa tvrtke"
      },
      "userAdministration": {
        "linkLabel": "Administracija korisnika"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Zaštita Podataka"
      },
      "imprint": {
        "linkLabel": "Impressum"
      },
      "termsAndConditions": {
        "linkLabel": "Uvjeti korištenja"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "lv-LV": {
    "company": {
      "label": "Kompānijas"
    },
    "accountSettings": {
      "label": "DutyPay Konta Iestatījumi",
      "accountInformation": {
        "linkLabel": "DutyPay Konts"
      },
      "securitySettings": {
        "linkLabel": "Drošības Iestatījumi"
      },
      "logout": {
        "linkLabel": "Atteikties"
      }
    },
    "productSettings": {
      "label": "{productName} Iestatījumi"
    },
    "feedforward": {
      "noCompany": {
        "message": "Nav norīkota uzņēmuma. Lūdzu, pievienojiet uzņēmumu."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Uzņēmuma Lestatījumi",
      "integrations": {
        "linkLabel": "Integrācijas iestatījumi"
      },
      "companyInformation": {
        "linkLabel": "Korporatīvais Konts"
      }
    },
    "developerSettings": {
      "label": "Izstrādātāja iestatījumi",
      "translationReport": {
        "linkLabel": "Tulkošanas ziņojums"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Izejošie ziņojumi"
      }
    },
    "adminSettings": {
      "label": "Administratora iestatījumi",
      "companyAdministration": {
        "linkLabel": "Uzņēmuma konta administrēšana"
      },
      "userAdministration": {
        "linkLabel": "Lietotāju administrēšana"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Datu Aizsardzība"
      },
      "imprint": {
        "linkLabel": "Kompānijas informācija"
      },
      "termsAndConditions": {
        "linkLabel": "Lietošanas noteikumi"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "lt-LT": {
    "company": {
      "label": "Įmonės"
    },
    "accountSettings": {
      "label": "DutyPay Paskyros Nustatymai",
      "accountInformation": {
        "linkLabel": "DutyPay Sąskaita"
      },
      "securitySettings": {
        "linkLabel": "Apsaugos Nustatymai"
      },
      "logout": {
        "linkLabel": "Atsijungti"
      }
    },
    "productSettings": {
      "label": "{productName} Nustatymai"
    },
    "feedforward": {
      "noCompany": {
        "message": "Nėra paskirtos įmonės. Pridėkite įmonę."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Įmonės Nustatymai",
      "integrations": {
        "linkLabel": "Integravimo parametrai"
      },
      "companyInformation": {
        "linkLabel": "Įmonės Sąskaita"
      }
    },
    "developerSettings": {
      "label": "Kūrėjo nustatymai",
      "translationReport": {
        "linkLabel": "Vertimo ataskaita"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Išeinantys pranešimai"
      }
    },
    "adminSettings": {
      "label": "Administratoriaus nustatymai",
      "companyAdministration": {
        "linkLabel": "Įmonės sąskaitos administravimas"
      },
      "userAdministration": {
        "linkLabel": "Vartotojo administravimas"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Duomenų Apsauga"
      },
      "imprint": {
        "linkLabel": "Kompanijos informacija"
      },
      "termsAndConditions": {
        "linkLabel": "Naudojimo sąlygos"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "pt-PT": {
    "company": {
      "label": "Empresas"
    },
    "accountSettings": {
      "label": "Configurações da Conta DutyPay",
      "accountInformation": {
        "linkLabel": "Conta DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Definições de Segurança"
      },
      "logout": {
        "linkLabel": "Sair"
      }
    },
    "productSettings": {
      "label": "{productName} Configurações"
    },
    "feedforward": {
      "noCompany": {
        "message": "Nenhuma empresa atribuída. Adicione uma empresa."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Configurações da Empresa",
      "integrations": {
        "linkLabel": "Configurações de integração"
      },
      "companyInformation": {
        "linkLabel": "Conta Corporativa"
      }
    },
    "developerSettings": {
      "label": "Configurações do desenvolvedor",
      "translationReport": {
        "linkLabel": "Relatório de tradução"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Mensagens de Saída"
      }
    },
    "adminSettings": {
      "label": "Configurações de administrador",
      "companyAdministration": {
        "linkLabel": "Administração de contas corporativas"
      },
      "userAdministration": {
        "linkLabel": "Administração do usuário"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Proteção de Dados"
      },
      "imprint": {
        "linkLabel": "Informações da Empresa"
      },
      "termsAndConditions": {
        "linkLabel": "Termos de uso"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "ro-RO": {
    "company": {
      "label": "Întreprindere"
    },
    "accountSettings": {
      "label": "Setările Contului DutyPay",
      "accountInformation": {
        "linkLabel": "Cont DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Setări de Securitate"
      },
      "logout": {
        "linkLabel": "Deconectare"
      }
    },
    "productSettings": {
      "label": "{productName} Setări"
    },
    "feedforward": {
      "noCompany": {
        "message": "Nicio companie desemnată. Vă rugăm să adăugați o companie."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Setări Companie",
      "integrations": {
        "linkLabel": "Setări de integrare"
      },
      "companyInformation": {
        "linkLabel": "Cont Corporativ"
      }
    },
    "developerSettings": {
      "label": "Setări pentru dezvoltatori",
      "translationReport": {
        "linkLabel": "Raport de traducere"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Ausgehende Nachricht"
      }
    },
    "adminSettings": {
      "label": "Setări administrator",
      "companyAdministration": {
        "linkLabel": "Administrarea contului companiei"
      },
      "userAdministration": {
        "linkLabel": "Administrarea utilizatorilor"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Protejarea Datelor"
      },
      "imprint": {
        "linkLabel": "Informații privind Compania"
      },
      "termsAndConditions": {
        "linkLabel": "Termeni de utilizare"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "ru-RU": {
    "company": {
      "label": "Компании"
    },
    "accountSettings": {
      "label": "Настройки учетной записи DutyPay",
      "accountInformation": {
        "linkLabel": "Аккаунт DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Настройки Безопасности"
      },
      "logout": {
        "linkLabel": "Выход"
      }
    },
    "productSettings": {
      "label": "{productName} настройки"
    },
    "feedforward": {
      "noCompany": {
        "message": "Нет назначенной компании. Пожалуйста, добавьте компанию."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Настройки Kомпании",
      "integrations": {
        "linkLabel": "Настройки интеграции"
      },
      "companyInformation": {
        "linkLabel": "Корпоративный Счет"
      }
    },
    "developerSettings": {
      "label": "Настройки разработчика",
      "translationReport": {
        "linkLabel": "Отчет о переводе"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Исходящие сообщения"
      }
    },
    "adminSettings": {
      "label": "Настройки администратора",
      "companyAdministration": {
        "linkLabel": "Управление корпоративным аккаунтом"
      },
      "userAdministration": {
        "linkLabel": "Администрирование пользователей"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Защита Данных"
      },
      "imprint": {
        "linkLabel": "Импрессум"
      },
      "termsAndConditions": {
        "linkLabel": "Условия эксплуатации"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "sv-SE": {
    "company": {
      "label": "Företag"
    },
    "accountSettings": {
      "label": "DutyPay-Kontoinställningar",
      "accountInformation": {
        "linkLabel": "DutyPay-Konto"
      },
      "securitySettings": {
        "linkLabel": "Säkerhetsinställningar"
      },
      "logout": {
        "linkLabel": "Logga ut"
      }
    },
    "productSettings": {
      "label": "{productName} Inställningar"
    },
    "feedforward": {
      "noCompany": {
        "message": "Inget tilldelat företag. Lägg till ett företag."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Företagsinställningar",
      "integrations": {
        "linkLabel": "Integrationsinställningar"
      },
      "companyInformation": {
        "linkLabel": "Företagskonto"
      }
    },
    "developerSettings": {
      "label": "Utvecklarinställningar",
      "translationReport": {
        "linkLabel": "Översättningsrapport"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Utgående meddelanden"
      }
    },
    "adminSettings": {
      "label": "Administratörsinställningar",
      "companyAdministration": {
        "linkLabel": "Administration av företagskonton"
      },
      "userAdministration": {
        "linkLabel": "Användaradministration"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Dataskydd"
      },
      "imprint": {
        "linkLabel": "Företagsinformation"
      },
      "termsAndConditions": {
        "linkLabel": "Villkor"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "sk-SK": {
    "company": {
      "label": "Podnik"
    },
    "accountSettings": {
      "label": "Nastavenia Účtu DutyPay",
      "accountInformation": {
        "linkLabel": "Účet DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Bezpečnostné Nastavenia"
      },
      "logout": {
        "linkLabel": "Odhlásiť sa"
      }
    },
    "productSettings": {
      "label": "{productName} Nastavenie"
    },
    "feedforward": {
      "noCompany": {
        "message": "Žiadna pridelená spoločnosť. Pridajte spoločnosť."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Nastavenia Spoločnosti",
      "integrations": {
        "linkLabel": "Nastavenia integrácie"
      },
      "companyInformation": {
        "linkLabel": "Firemný Účet"
      }
    },
    "developerSettings": {
      "label": "Nastavenia pre vývojárov",
      "translationReport": {
        "linkLabel": "Správa o preklade"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Odchádzajúce správy"
      }
    },
    "adminSettings": {
      "label": "Nastavenia správcu",
      "companyAdministration": {
        "linkLabel": "Správa firemného účtu"
      },
      "userAdministration": {
        "linkLabel": "Administrácia užívateľov"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Ochrana Dát"
      },
      "imprint": {
        "linkLabel": "Impressum"
      },
      "termsAndConditions": {
        "linkLabel": "Podmienky používania"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "cs-CZ": {
    "company": {
      "label": "Společnost"
    },
    "accountSettings": {
      "label": "Nastavení Účtu DutyPay",
      "accountInformation": {
        "linkLabel": "Účet DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Bezpečnostní Nastavení"
      },
      "logout": {
        "linkLabel": "Odhlásit se"
      }
    },
    "productSettings": {
      "label": "{productName} Nastavení"
    },
    "feedforward": {
      "noCompany": {
        "message": "Žádná přiřazená společnost. Přidejte společnost."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Nastavení Společnosti",
      "integrations": {
        "linkLabel": "Nastavení integrace"
      },
      "companyInformation": {
        "linkLabel": "Firemní Účet"
      }
    },
    "developerSettings": {
      "label": "Nastavení vývojáře",
      "translationReport": {
        "linkLabel": "Zpráva o překladu"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Odchozí zprávy"
      }
    },
    "adminSettings": {
      "label": "Nastavení správce",
      "companyAdministration": {
        "linkLabel": "Správa firemního účtu"
      },
      "userAdministration": {
        "linkLabel": "Správa uživatelů"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Ochrana Dat"
      },
      "imprint": {
        "linkLabel": "Impresum"
      },
      "termsAndConditions": {
        "linkLabel": "Podmínky použití"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "tr-TR": {
    "company": {
      "label": "Şirket"
    },
    "accountSettings": {
      "label": "DutyPay Hesap Ayarları",
      "accountInformation": {
        "linkLabel": "DutyPay Hesabı"
      },
      "securitySettings": {
        "linkLabel": "Güvenlik Ayarları"
      },
      "logout": {
        "linkLabel": "Çıkış Yap"
      }
    },
    "productSettings": {
      "label": "{productName} Ayarlar"
    },
    "feedforward": {
      "noCompany": {
        "message": "Atanmış şirket yok. Lütfen bir şirket ekleyin."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Şirket Ayarları",
      "integrations": {
        "linkLabel": "Entegrasyon ayarları"
      },
      "companyInformation": {
        "linkLabel": "Kurumsal Hesap"
      }
    },
    "developerSettings": {
      "label": "Geliştirici Ayarları",
      "translationReport": {
        "linkLabel": "Çeviri raporu"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Giden Mesajlar"
      }
    },
    "adminSettings": {
      "label": "Yönetici ayarları",
      "companyAdministration": {
        "linkLabel": "Şirket hesap yönetimi"
      },
      "userAdministration": {
        "linkLabel": "Kullanıcı yönetimi"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Veri Koruması"
      },
      "imprint": {
        "linkLabel": "Şirket Bilgisi"
      },
      "termsAndConditions": {
        "linkLabel": "Kullanım Şartları"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "hu-HU": {
    "company": {
      "label": "Vállalat"
    },
    "accountSettings": {
      "label": "DutyPay Fiókbeállítások",
      "accountInformation": {
        "linkLabel": "DutyPay Számla"
      },
      "securitySettings": {
        "linkLabel": "Biztonsági Beállítások"
      },
      "logout": {
        "linkLabel": "Kijelentkezés"
      }
    },
    "productSettings": {
      "label": "{productName} Beállítások"
    },
    "feedforward": {
      "noCompany": {
        "message": "Nincs kijelölt vállalat. Kérjük, adjon hozzá egy vállalatot."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Vállalati Beállítások",
      "integrations": {
        "linkLabel": "Integrációs beállítások"
      },
      "companyInformation": {
        "linkLabel": "Vállalati Számla"
      }
    },
    "developerSettings": {
      "label": "Fejlesztői beállítások",
      "translationReport": {
        "linkLabel": "Fordítási jelentés"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Kimenő üzenetek"
      }
    },
    "adminSettings": {
      "label": "Rendszergazda beállításai",
      "companyAdministration": {
        "linkLabel": "Vállalati számla adminisztráció"
      },
      "userAdministration": {
        "linkLabel": "Felhasználói adminisztráció"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Adat Védelem"
      },
      "imprint": {
        "linkLabel": "Céginformáció"
      },
      "termsAndConditions": {
        "linkLabel": "Használati feltételek"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "fr-BE": {
    "company": {
      "label": "Entreprises"
    },
    "accountSettings": {
      "label": "Paramètres du Compte DutyPay",
      "accountInformation": {
        "linkLabel": "Compte DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Les Paramètres de Sécurité"
      },
      "logout": {
        "linkLabel": "Se déconnecter"
      }
    },
    "productSettings": {
      "label": "{productName} Paramètres"
    },
    "feedforward": {
      "noCompany": {
        "message": "Aucune entreprise assignée. Veuillez ajouter une entreprise."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Paramètres de l'Entreprise",
      "integrations": {
        "linkLabel": "Paramètres d'intégration"
      },
      "companyInformation": {
        "linkLabel": "Compte d'Entreprise"
      }
    },
    "developerSettings": {
      "label": "Paramètres du développeur",
      "translationReport": {
        "linkLabel": "Rapport de traduction"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Messages sortants"
      }
    },
    "adminSettings": {
      "label": "Paramètres administrateur",
      "companyAdministration": {
        "linkLabel": "Gestion des comptes d'entreprise"
      },
      "userAdministration": {
        "linkLabel": "Gestion des utilisateurs"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Protection des Données"
      },
      "imprint": {
        "linkLabel": "Mentions Légales"
      },
      "termsAndConditions": {
        "linkLabel": "Conditions d'utilisation"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "fr-LU": {
    "company": {
      "label": "Entreprises"
    },
    "accountSettings": {
      "label": "Paramètres du Compte DutyPay",
      "accountInformation": {
        "linkLabel": "Compte DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Les Paramètres de Sécurité"
      },
      "logout": {
        "linkLabel": "Se déconnecter"
      }
    },
    "productSettings": {
      "label": "{productName} Paramètres"
    },
    "feedforward": {
      "noCompany": {
        "message": "Aucune entreprise assignée. Veuillez ajouter une entreprise."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Paramètres de l'Entreprise",
      "integrations": {
        "linkLabel": "Paramètres d'intégration"
      },
      "companyInformation": {
        "linkLabel": "Compte d'Entreprise"
      }
    },
    "developerSettings": {
      "label": "Paramètres du développeur",
      "translationReport": {
        "linkLabel": "Rapport de traduction"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Messages sortants"
      }
    },
    "adminSettings": {
      "label": "Paramètres administrateur",
      "companyAdministration": {
        "linkLabel": "Gestion des comptes d'entreprise"
      },
      "userAdministration": {
        "linkLabel": "Gestion des utilisateurs"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Protection des Données"
      },
      "imprint": {
        "linkLabel": "Mentions Légales"
      },
      "termsAndConditions": {
        "linkLabel": "Conditions d'utilisation"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "mt-MT": {
    "company": {
      "label": "Kumpaniji"
    },
    "accountSettings": {
      "label": "Settings tal-Kont DutyPay",
      "accountInformation": {
        "linkLabel": "Kont DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Settings tas-Sigurtà"
      },
      "logout": {
        "linkLabel": "Oħroġ"
      }
    },
    "productSettings": {
      "label": "{productName} Settings"
    },
    "feedforward": {
      "noCompany": {
        "message": "L-ebda kumpanija assenjata. Jekk jogħġbok żid kumpanija."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Settings tal-Kumpanija",
      "integrations": {
        "linkLabel": "Is-settings tal-integrazzjoni"
      },
      "companyInformation": {
        "linkLabel": "Kont Korporattiv"
      }
    },
    "developerSettings": {
      "label": "Settings tal-Iżviluppatur",
      "translationReport": {
        "linkLabel": "Rapport tat-traduzzjoni"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Messaġġ li joħroġ"
      }
    },
    "adminSettings": {
      "label": "Is-settings tal-amministratur",
      "companyAdministration": {
        "linkLabel": "Amministrazzjoni tal-kont tal-kumpanija"
      },
      "userAdministration": {
        "linkLabel": "Amministrazzjoni tal-utent"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Protezzjoni tad-Dejta"
      },
      "imprint": {
        "linkLabel": ""
      },
      "termsAndConditions": {
        "linkLabel": "Termini ta 'Użu"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "sl-SI": {
    "company": {
      "label": "Podjetje"
    },
    "accountSettings": {
      "label": "Nastavitve Računa DutyPay",
      "accountInformation": {
        "linkLabel": "Račun DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Varnostne Nastavitve"
      },
      "logout": {
        "linkLabel": "Odjava"
      }
    },
    "productSettings": {
      "label": "{productName} Nastavitve"
    },
    "feedforward": {
      "noCompany": {
        "message": "Ni dodeljenega podjetja. Dodajte podjetje."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Nastavitve Podjetja",
      "integrations": {
        "linkLabel": "Nastavitve integracije"
      },
      "companyInformation": {
        "linkLabel": "Račun Podjetja"
      }
    },
    "developerSettings": {
      "label": "Nastavitve za razvijalce",
      "translationReport": {
        "linkLabel": "Poročilo o prevodu"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Odhodna sporočila"
      }
    },
    "adminSettings": {
      "label": "Nastavitve skrbnika",
      "companyAdministration": {
        "linkLabel": "Administracija računa podjetja"
      },
      "userAdministration": {
        "linkLabel": "Administracija uporabnikov"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Varstvo Podatkov"
      },
      "imprint": {
        "linkLabel": "Informacije o podjetju"
      },
      "termsAndConditions": {
        "linkLabel": "Pogoji uporabe"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "el-CY": {
    "company": {
      "label": "Εταιρείες"
    },
    "accountSettings": {
      "label": "Ρυθμίσεις Λογαριασμού DutyPay",
      "accountInformation": {
        "linkLabel": "Λογαριασμός DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Ρυθμίσεις Ασφαλείας"
      },
      "logout": {
        "linkLabel": "Αποσύνδεση"
      }
    },
    "productSettings": {
      "label": "{productName} Ρυθμίσεις"
    },
    "feedforward": {
      "noCompany": {
        "message": "Δεν έχει ανατεθεί εταιρεία. Προσθέστε μια εταιρεία."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Ρυθμίσεις Εταιρείας",
      "integrations": {
        "linkLabel": "Ρυθμίσεις ενοποίησης"
      },
      "companyInformation": {
        "linkLabel": "Εταιρικός Λογαριασμός"
      }
    },
    "developerSettings": {
      "label": "Ρυθμίσεις προγραμματιστή",
      "translationReport": {
        "linkLabel": "Έκθεση μετάφρασης"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Εξερχόμενα μηνύματα"
      }
    },
    "adminSettings": {
      "label": "Ρυθμίσεις διαχειριστή",
      "companyAdministration": {
        "linkLabel": "Διαχείριση εταιρικού λογαριασμού"
      },
      "userAdministration": {
        "linkLabel": "Διαχείριση χρηστών"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Προστασία Δεδομένων"
      },
      "imprint": {
        "linkLabel": "Στοιχεία της εταιρείας"
      },
      "termsAndConditions": {
        "linkLabel": "Οροι χρήσης"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "tr-CY": {
    "company": {
      "label": "Şirket"
    },
    "accountSettings": {
      "label": "DutyPay Hesap Ayarları",
      "accountInformation": {
        "linkLabel": "DutyPay Hesabı"
      },
      "securitySettings": {
        "linkLabel": "Güvenlik Ayarları"
      },
      "logout": {
        "linkLabel": "Çıkış Yap"
      }
    },
    "productSettings": {
      "label": "{productName} Ayarlar"
    },
    "feedforward": {
      "noCompany": {
        "message": "Atanmış şirket yok. Lütfen bir şirket ekleyin."
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "Şirket Ayarları",
      "integrations": {
        "linkLabel": "Entegrasyon ayarları"
      },
      "companyInformation": {
        "linkLabel": "Kurumsal Hesap"
      }
    },
    "developerSettings": {
      "label": "Geliştirici Ayarları",
      "translationReport": {
        "linkLabel": "Çeviri raporu"
      },
      "translationEmailReportOverview": {
        "linkLabel": "Giden Mesajlar"
      }
    },
    "adminSettings": {
      "label": "Yönetici ayarları",
      "companyAdministration": {
        "linkLabel": "Şirket hesap yönetimi"
      },
      "userAdministration": {
        "linkLabel": "Kullanıcı yönetimi"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Veri Koruması"
      },
      "imprint": {
        "linkLabel": "Şirket Bilgisi"
      },
      "termsAndConditions": {
        "linkLabel": "Kullanım Şartları"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  },
  "uk-UA": {
    "company": {
      "label": "Підприємство"
    },
    "accountSettings": {
      "label": "Налаштування Облікового Запису DutyPay",
      "accountInformation": {
        "linkLabel": "Рахунок DutyPay"
      },
      "securitySettings": {
        "linkLabel": "Параметри Безпеки"
      },
      "logout": {}
    },
    "productSettings": {},
    "feedforward": {
      "noCompany": {}
    },
    "companySettings": {
      "label": "Налаштування Kомпанії",
      "integrations": {
        "linkLabel": "Налаштування Інтеграції"
      },
      "companyInformation": {
        "linkLabel": "Рахунок Kомпанії"
      }
    },
    "developerSettings": {
      "label": "Налаштування Розробника",
      "translationReport": {},
      "translationEmailReportOverview": {}
    },
    "adminSettings": {
      "label": "Налаштування Адміністратора",
      "companyAdministration": {},
      "userAdministration": {}
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "Захист Даних"
      },
      "imprint": {},
      "termsAndConditions": {}
    },
    "copyrightNotice": {}
  },
  "zh-CN": {
    "company": {
      "label": "公司介绍"
    },
    "accountSettings": {
      "label": "DutyPay 账户设置",
      "accountInformation": {
        "linkLabel": "DutyPay 帐户"
      },
      "securitySettings": {
        "linkLabel": "安全设定"
      },
      "logout": {
        "linkLabel": "登出"
      }
    },
    "productSettings": {
      "label": "{productName} 设定值"
    },
    "feedforward": {
      "noCompany": {
        "message": "没有指定的公司。 请添加公司。"
      }
    },
    "notifications": "",
    "companySettings": {
      "label": "公司设定",
      "integrations": {
        "linkLabel": "集成设置"
      },
      "companyInformation": {
        "linkLabel": "企业账户"
      }
    },
    "developerSettings": {
      "label": "开发人员设定",
      "translationReport": {
        "linkLabel": "翻译报告"
      },
      "translationEmailReportOverview": {
        "linkLabel": "外发邮件"
      }
    },
    "adminSettings": {
      "label": "管理员设置",
      "companyAdministration": {
        "linkLabel": "公司账户管理"
      },
      "userAdministration": {
        "linkLabel": "用户管理"
      }
    },
    "legalLinks": {
      "privacyPolicy": {
        "linkLabel": "数据保护"
      },
      "imprint": {
        "linkLabel": "公司信息"
      },
      "termsAndConditions": {
        "linkLabel": "使用条款"
      }
    },
    "copyrightNotice": {
      "label": "© 2013 - {currentDateDisplayOnlyYear} - DutyPay GmbH"
    }
  }
}
</i18n>
